import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Phone, ArrowRight } from "lucide-react";
import { restaurantInfo } from "../data/mock";
import lunchSections from "../data/lunchMenu";
import {
  HERO_VIDEO,
  HERO_IMAGE,
  WINES_AND_LOGO,
  GARDEN,
  FEATURE_ICONS,
  FORM_PATTERN,
  OFFERINGS_1,
  OFFERINGS_2,
  OFFERINGS_3,
} from "../data/assets";
import Reveal from "../components/Reveal";
import { useI18n } from "../data/i18n";

// Keep the lunch menu teaser ready to restore without deleting its content.
const SHOW_LUNCH_MENU = true;

const DiamondDivider = () => (
  <div className="flex items-center justify-center gap-2 my-4">
    <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#e4c590]"></div>
    <span className="diamond-mark"></span>
    <span className="diamond-mark opacity-60"></span>
    <span className="diamond-mark"></span>
    <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#e4c590]"></div>
  </div>
);

// Филтър функция – премахва items с active: false и добавя правилен език
const getActiveSections = (sections, lang) => {
  return sections
    .map(section => ({
      title: lang === 'bg' ? section.title : section.title_en,
      items: section.items
        .filter(item => item.active !== false)
        .map(item => ({
          name: lang === 'bg' ? item.name : item.name_en,
          desc: lang === 'bg' ? (item.desc || "") : (item.desc_en || ""),
          weight: lang === 'bg' ? item.weight : item.weight_en,
          price: item.price,
        }))
    }))
    .filter(section => section.items.length > 0);
};

// ============================================
// SCROLL SNAP WRAPPER ЗА OFFERINGS
// ============================================
const OfferingsScrollSnap = ({ children }) => {
  const sectionRef = useRef(null);
  const [isSnapped, setIsSnapped] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Когато секцията е видима, активираме снап ефекта
        setIsSnapped(entry.isIntersecting && entry.intersectionRatio > 0.3);
      },
      {
        threshold: [0.3, 0.5, 0.7],
        rootMargin: '0px',
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div 
      ref={sectionRef}
      className={`relative transition-all duration-500 ${
        isSnapped ? 'snap-active' : ''
      }`}
    >
      {children}
    </div>
  );
};

// ============================================
// MAIN HOME COMPONENT
// ============================================
const Home = () => {
  const { t, lang } = useI18n();
  
  const activeLunchSections = getActiveSections(lunchSections, lang);
  
  const offerings = [
    { 
      title: t("offerings.experience"), 
      cta: t("offerings.experience_cta"), 
      link: "/reservation", 
      image: OFFERINGS_1,
    },
    { 
      title: t("offerings.tastes"), 
      cta: t("offerings.see_menu"), 
      link: "/menu", 
      image: OFFERINGS_2,
    },
    { 
      title: t("offerings.wine"), 
      cta: t("offerings.see_menu"), 
      link: "/menu", 
      image: OFFERINGS_3,
    },
  ];
  
  const features = [
    { title: t("features.fresh"), desc: t("features.fresh_desc") },
    { title: t("features.tastes"), desc: t("features.tastes_desc") },
    { title: t("features.chefs"), desc: t("features.chefs_desc") },
    { title: t("features.events"), desc: t("features.events_desc") },
  ];

  return (
    <div className="bg-[#0a0a0a]">
      {/* ============================================
          HERO SECTION
          ============================================ */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <video 
          autoPlay 
          loop 
          muted 
          playsInline 
          poster={HERO_IMAGE} 
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/40 via-[#0a0a0a]/30 to-[#0a0a0a]/70 pointer-events-none"></div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-32 pb-20">
          <Reveal direction="up" delay={100}>
            <p className="text-[#cfc7b8] uppercase tracking-[0.4em] text-xs mb-4">{t("contact.street")}</p>
          </Reveal>
          <Reveal direction="fade" delay={250}><DiamondDivider /></Reveal>
          <Reveal direction="up" delay={400}>
            <h1 className="display text-5xl md:text-7xl lg:text-8xl text-[#f5f0e6] font-light leading-tight mb-6">
              {t("hero.welcome")}<br /><span className="text-[#f5f0e6]">CASA BALA</span>
            </h1>
          </Reveal>
          <Reveal direction="up" delay={600}>
            <p className="text-[#cfc7b8] text-lg md:text-xl mb-10 italic">{t("hero.tagline")}</p>
          </Reveal>
          <Reveal direction="up" delay={800}>
            <Link to="/reservation" className="cb-btn cb-btn-gold">
              <span className="cb-btn-text cb-btn-text-1">{t("hero.reservation")}</span>
              <span className="cb-btn-text cb-btn-text-2" aria-hidden="true">{t("hero.reservation")}</span>
            </Link>
          </Reveal>
        </div>
        <button 
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: "smooth" })} 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#e4c590] animate-bounce z-10" 
          aria-label="Scroll down"
        >
          <ChevronDown size={28} />
        </button>
      </section>

{/* ============================================
    OFFERINGS SECTION – КОРИГИРАН ЕФЕКТ
    ============================================ */}
<OfferingsScrollSnap>
  <section className="min-h-screen py-24 lg:py-32 bg-[#0a0a0a] relative overflow-hidden flex items-center">
    <div className="absolute inset-0 opacity-20 pointer-events-none">
      <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-[#e4c590]/5 rounded-full blur-[120px]"></div>
      <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-[#e4c590]/5 rounded-full blur-[120px]"></div>
    </div>

    <div className="max-w-7xl mx-auto px-6 relative w-full">
      <div className="text-center mb-16">
        <Reveal direction="up">
          <p className="section-label text-[#e4c590]/70">{t("offerings.label")}</p>
        </Reveal>
        <Reveal direction="up" delay={100}>
          <h2 className="display text-4xl md:text-5xl lg:text-6xl text-[#f5f0e6] mt-3 mb-4 font-light tracking-wide">
            {t("offerings.title")}
          </h2>
        </Reveal>
        <Reveal direction="fade" delay={250}>
          <DiamondDivider />
        </Reveal>
        <Reveal direction="up" delay={300}>
          <p className="text-[#cfc7b8] italic max-w-2xl mx-auto text-base md:text-lg">
            {t("offerings.welcome")}
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
        {offerings.map((o, idx) => (
          <Reveal key={idx} direction="up" delay={idx * 150}>
            {/* Цялата карта като Link - без active ефекти */}
            <Link 
              to={o.link}
              className="group relative flex flex-col items-center text-center cursor-pointer block no-underline"
            >
              {/* Изображение с рамка и орнамент */}
              <div className="relative w-full aspect-[4/5] overflow-hidden mb-6">
                {/* Hover overlay - градиент */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/80 via-transparent to-transparent z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                
                {/* Рамка */}
                <div className="absolute inset-0 border border-[#e4c590]/20 group-hover:border-[#e4c590] transition-all duration-700 z-20"></div>
                
                {/* Орнаменти - ъгли */}
                <div className="absolute -top-1 -left-1 w-8 h-8 border-t-2 border-l-2 border-[#e4c590]/40 group-hover:border-[#e4c590] transition-colors duration-700 z-20"></div>
                <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-2 border-r-2 border-[#e4c590]/40 group-hover:border-[#e4c590] transition-colors duration-700 z-20"></div>
                <div className="absolute -top-1 -right-1 w-8 h-8 border-t-2 border-r-2 border-[#e4c590]/20 group-hover:border-[#e4c590]/40 transition-colors duration-700 z-20"></div>
                <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-2 border-l-2 border-[#e4c590]/20 group-hover:border-[#e4c590]/40 transition-colors duration-700 z-20"></div>
                
                <img 
                  src={o.image} 
                  alt={o.title} 
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" 
                  loading="lazy"
                  decoding="async"
                />
                
                {/* Златист ореол при hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-gradient-to-t from-[#e4c590]/15 via-transparent to-transparent z-10"></div>
              </div>

              {/* Заглавие */}
              <h3 className="display text-2xl md:text-3xl text-[#f5f0e6] mb-3 font-light tracking-wide group-hover:text-[#e4c590] transition-colors duration-500">
                {o.title}
              </h3>

              {/* Декоративна линия */}
              <div className="w-12 h-px bg-[#e4c590]/40 group-hover:bg-[#e4c590] transition-all duration-700 group-hover:w-20 mb-4"></div>

              {/* CTA текст */}
              <div className="relative inline-flex items-center gap-2 text-[#cfc7b8] uppercase tracking-[0.25em] text-xs group-hover:text-[#e4c590] transition-colors duration-500">
                <span className="relative">
                  {o.cta}
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#e4c590] group-hover:w-full transition-all duration-700 ease-out"></span>
                </span>
                <ArrowRight 
                  size={12} 
                  className="opacity-0 group-hover:opacity-100 transition-all duration-500 -translate-x-3 group-hover:translate-x-0" 
                />
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
</OfferingsScrollSnap>



{/* ============================================
    ABOUT STORY SECTION – ВАРИАНТ 10: РАЗМЕНЕН
    ============================================ */}
<section className="py-24 lg:py-32 bg-[#0a0a0a] relative overflow-hidden">
  <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center relative">
    
    {/* ЛЯВО - ИЗОБРАЖЕНИЯ (първи) */}
    <Reveal direction="left">
      <div className="relative">
        <div className="relative aspect-[4/5] overflow-hidden group">
          <img 
            src={GARDEN} 
            alt="CASA BALA Garden" 
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-[#e4c590]/0 group-hover:bg-[#e4c590]/5 transition-colors duration-500"></div>
        </div>
        
        <div className="hidden md:block absolute -bottom-8 -right-8 lg:-bottom-12 lg:-right-12 w-[45%] aspect-[3/4] z-10">
          <div className="relative h-full bg-[#0a0a0a] p-2 border-2 border-[#e4c590]">
            <img 
              src={WINES_AND_LOGO} 
              alt="Wines" 
              className="w-full h-full object-cover" 
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </Reveal>

    {/* ДЯСНО - ТЕКСТ (втори) */}
    <Reveal direction="right" delay={200}>
      <div className="lg:pl-4">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-8 h-px bg-[#e4c590]"></div>
          <p className="section-label text-[#e4c590]/60 tracking-[0.3em] mb-0">
            {t("story.label")}
          </p>
        </div>
        
        <h2 className="display text-4xl md:text-5xl lg:text-6xl text-[#f5f0e6] mb-6 leading-[1.1]">
          {t("story.title")}
        </h2>
        
        <div className="w-16 h-px bg-gradient-to-r from-[#e4c590] to-transparent mb-6"></div>
        
        <p className="text-[#cfc7b8] leading-relaxed mb-8 text-base md:text-lg">
          {t("story.body")}
        </p>
        
        <div className="flex items-center gap-6 mb-8">
          <div className="flex items-center gap-3">
            <Phone size={16} className="text-[#e4c590]" />
            <a 
              href={`tel:${restaurantInfo.phone}`} 
              className="text-[#e4c590] hover:text-[#f5f0e6] transition-colors text-lg"
            >
              {restaurantInfo.phone}
            </a>
          </div>
          <span className="w-px h-6 bg-[#e4c590]/20"></span>
          <p className="text-[#a8a297] text-sm">{t("story.call")}</p>
        </div>
        
        <Link to="/about" className="cb-btn cb-btn-gold">
          <span className="cb-btn-text cb-btn-text-1">{t("story.more")}</span>
          <span className="cb-btn-text cb-btn-text-2" aria-hidden="true">{t("story.more")}</span>
        </Link>
      </div>
    </Reveal>
    
  </div>
</section>

      {/* ============================================
          LUNCH MENU TEASER SECTION
          ============================================ */}
      {SHOW_LUNCH_MENU && (
      <section className="py-24 lg:py-32 bg-[#070707] relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 relative">
          <div className="text-center mb-12">
            <Reveal direction="up">
              <p className="section-label">{t("menu.lunch_hours")}</p>
            </Reveal>
            <Reveal direction="up" delay={100}>
              <h2 className="display text-4xl md:text-5xl lg:text-6xl text-[#f5f0e6] mt-3">
                {t("menu.lunch")}
              </h2>
            </Reveal>
            <Reveal direction="fade" delay={250}>
              <DiamondDivider />
            </Reveal>
            <Reveal direction="up" delay={300}>
              <p className="text-[#cfc7b8] italic max-w-2xl mx-auto">
                {t("menu.lunch_desc")}
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10 mb-12">
            {activeLunchSections.map((section, sIdx) => (
              <Reveal key={sIdx} direction="up" delay={sIdx * 120}>
                <div>
                  <h3 className="display text-3xl text-[#e4c590] uppercase tracking-widest text-center mb-2">
                    {section.title}
                  </h3>
                  <div className="flex items-center justify-center gap-1 mb-6">
                    <span className="diamond-mark opacity-60"></span>
                    <span className="diamond-mark"></span>
                    <span className="diamond-mark opacity-60"></span>
                  </div>
                  <div className="space-y-5">
                    {section.items.map((item, idx) => (
                      <div key={idx} className="group">
                        <div className="flex items-baseline justify-between gap-3 mb-1">
                          <h4 className="display text-xl md:text-2xl text-[#f5f0e6] group-hover:text-[#e4c590] transition-colors leading-snug">
                            {item.name}
                          </h4>
                          <span className="text-[#e4c590] text-base md:text-lg whitespace-nowrap flex-shrink-0">
                            {item.price}
                          </span>
                        </div>
                        {item.weight && (
                          <p className="text-[#7a7468] text-xs uppercase tracking-widest">
                            {item.weight}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal direction="up">
            <div className="text-center">
              <Link to="/menu/lunch" className="cb-btn cb-btn-outline inline-flex items-center gap-2">
                <span className="cb-btn-text cb-btn-text-1 inline-flex items-center gap-2">
                  {t("menu.see_menu")} <ArrowRight size={14} />
                </span>
                <span className="cb-btn-text cb-btn-text-2 inline-flex items-center gap-2" aria-hidden="true">
                  {t("menu.see_menu")} <ArrowRight size={14} />
                </span>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
      )}

      {/* ============================================
          RESERVATIONS + CONTACT SECTION
          ============================================ */}
      <section className="py-24 lg:py-32 bg-[#070707] relative">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-0 border border-[#1f1f1f]">
          <Reveal direction="left">
            <div className="p-10 lg:p-16 text-center bg-[#0a0a0a] h-full">
              <h2 className="display text-4xl md:text-5xl text-[#f5f0e6] mb-3">
                {t("res.title")}
              </h2>
              <p className="text-[#cfc7b8] italic mb-8">{t("res.only_phone")}</p>
              <DiamondDivider />
              <p className="text-[#cfc7b8] mt-8 mb-4">{t("res.call_to_reserve")}</p>
              <a href={`tel:${restaurantInfo.phone}`} className="cb-btn cb-btn-gold inline-block">
                <span className="cb-btn-text cb-btn-text-1">{restaurantInfo.phone}</span>
                <span className="cb-btn-text cb-btn-text-2" aria-hidden="true">{restaurantInfo.phone}</span>
              </a>
              <div className="mt-10">
                <p className="text-[#e4c590] uppercase tracking-widest text-xs mb-2">
                  {t("res.hours_label")}
                </p>
                <p className="text-[#cfc7b8]">{t("common.reservation_hours")}</p>
              </div>
            </div>
          </Reveal>

          <Reveal direction="right" delay={150}>
            <div 
              className="p-10 lg:p-16 text-center relative overflow-hidden h-full"
              style={{ 
                backgroundImage: `url(${FORM_PATTERN})`, 
                backgroundSize: "cover", 
                backgroundColor: "#0c0c0c" 
              }}
            >
              <div className="absolute inset-0 bg-[#0c0c0c]/85"></div>
              <div className="relative">
                <h2 className="display text-4xl md:text-5xl text-[#f5f0e6] mb-8">
                  {t("res.contact_us")}
                </h2>
                <div className="mb-6">
                  <p className="text-[#e4c590] uppercase tracking-widest text-xs mb-2">
                    {t("res.location")}
                  </p>
                  <p className="text-[#cfc7b8]">{t("contact.street")}</p>
                  <p className="text-[#cfc7b8]">{t("contact.city")}</p>
                </div>
                <a href={`tel:${restaurantInfo.phone}`} className="text-[#e4c590] text-xl block mb-6 hover:opacity-80">
                  {restaurantInfo.phone}
                </a>
                <span className="diamond-mark inline-block"></span>
                <div className="mt-6">
                  <p className="text-[#e4c590] uppercase tracking-widest text-xs mb-2">
                    {t("res.work_hours")}
                  </p>
                  <p className="text-[#cfc7b8]">{t("common.work_days")}</p>
                  <p className="text-[#cfc7b8]">12:00 - 24:00</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============================================
          FEATURES SECTION
          ============================================ */}
      <section className="py-24 lg:py-32 bg-[#0a0a0a] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center mb-16">
            <Reveal direction="up">
              <p className="section-label">{t("features.label")}</p>
            </Reveal>
            <Reveal direction="up" delay={100}>
              <h2 className="display text-4xl md:text-5xl lg:text-6xl text-[#f5f0e6] mt-3">
                {t("features.title")}
              </h2>
            </Reveal>
            <Reveal direction="fade" delay={250}>
              <DiamondDivider />
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((f, idx) => (
              <Reveal key={idx} direction="up" delay={idx * 130}>
                <div className="text-center p-6 group">
                  <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                    <img 
                      src={FEATURE_ICONS[idx]} 
                      alt={f.title} 
                      className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110 group-hover:-translate-y-1" 
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <h3 className="display text-2xl text-[#f5f0e6] mb-3">{f.title}</h3>
                  <p className="text-[#a8a297] text-sm leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
